import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useSocket } from '../contexts/SocketContext';
import { SocketUser } from '../types';
import { 
  Card, 
  Button, 
  Input, 
  colors,
  spacing,
  typography,
  shadows,
  borderRadius
} from '../styles/GlobalStyles';
import { MessageCircle, Send, Users, ChevronDown, ChevronUp } from 'lucide-react';

const ChatContainer = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== 'isCollapsed'
})<{ isCollapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${props => props.isCollapsed ? 'auto' : '500px'};
  padding: 0;
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  transition: height 0.3s ease;
`;

const ChatHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isCollapsed'
})<{ isCollapsed?: boolean }>`
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.grey[800]};
  background: linear-gradient(180deg, #060606, #020202);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const ChatTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const ActiveUsersCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[200]};
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.full};
  padding: 2px ${spacing[2]};
  background: rgba(255, 255, 255, 0.02);
`;

const OnlineDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.success};
`;

const ChatMessages = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isCollapsed'
})<{ isCollapsed?: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  max-height: ${props => props.isCollapsed ? '0' : 'none'};
  opacity: ${props => props.isCollapsed ? '0' : '1'};
  transition: all 0.3s ease;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.grey[900]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.grey[600]};
    border-radius: ${borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.grey[500]};
  }
`;

const MessageItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOwnMessage'].includes(prop)
})<{ isOwnMessage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isOwnMessage ? 'flex-end' : 'flex-start'};
  max-width: 80%;
  align-self: ${props => props.isOwnMessage ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOwnMessage'].includes(prop)
})<{ isOwnMessage?: boolean }>`
  background: ${props => props.isOwnMessage ? colors.green : colors.grey[800]};
  color: ${props => props.isOwnMessage ? colors.black : colors.white};
  padding: ${spacing[3]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  line-height: 1.4;
  word-wrap: break-word;
  max-width: 100%;
  box-shadow: ${shadows.base};
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
`;

const Username = styled.span.withConfig({
  shouldForwardProp: (prop) => !['role'].includes(prop)
})<{ role?: string }>`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => {
    switch (props.role) {
      case 'admin':
        return colors.error;
      default:
        return colors.grey[300];
    }
  }};
`;

const Timestamp = styled.span`
  color: ${colors.grey[500]};
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  font-style: italic;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 2px;
`;

const Dot = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'delay'
})<{ delay: number }>`
  width: 4px;
  height: 4px;
  background: ${colors.grey[400]};
  border-radius: 50%;
  animation: typing 1.4s infinite;
  animation-delay: ${props => props.delay}s;
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }
`;

const ChatInput = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isCollapsed'
})<{ isCollapsed?: boolean }>`
  padding: ${spacing[3]};
  border-top: 1px solid ${colors.grey[800]};
  background: linear-gradient(180deg, #020202, #050505);
  display: flex;
  gap: ${spacing[2]};
  max-height: none;
  opacity: 1;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const MessageInput = styled(Input)`
  flex: 1;
  background: ${colors.grey[900]};
  border: 1px solid ${colors.grey[600]};
  color: ${colors.white};
  min-height: 46px;
  
  &:focus {
    border-color: ${colors.green};
    box-shadow: 0 0 0 2px ${colors.green}20;
  }
  
  &::placeholder {
    color: ${colors.grey[500]};
  }
`;

const SendButton = styled(Button)`
  min-width: 90px;
  padding: ${spacing[3]} ${spacing[4]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
`;

const SendLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.grey[400]};
  text-align: center;
  padding: ${spacing[6]};
  border: 1px dashed ${colors.grey[700]};
  border-radius: ${borderRadius.md};
  background: ${colors.grey[950]};
`;

const EmptyIcon = styled(MessageCircle)`
  width: 44px;
  height: 44px;
  margin-bottom: ${spacing[3]};
  opacity: 0.5;
`;

const EmptyTitle = styled.div`
  font-size: ${typography.fontSize.base};
  color: ${colors.grey[200]};
  font-weight: ${typography.fontWeight.semibold};
`;

const EmptyCopy = styled.div`
  margin-top: ${spacing[1]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

interface ChatProps {
  draftId: string;
  activeUsers: SocketUser[];
  currentUsername: string;
}

const Chat: React.FC<ChatProps> = ({ draftId, activeUsers, currentUsername }) => {
  const [newMessage, setNewMessage] = useState('');
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const previousMessageCount = useRef(0);
  
  const { socket, sendChatMessage, startTyping, stopTyping, chatMessages } = useSocket();

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Only scroll to bottom if there are new messages (not on initial load)
    if (chatMessages.length > previousMessageCount.current) {
      scrollToBottom();
    }
    
    // Check for new messages when collapsed
    if (chatMessages.length > previousMessageCount.current && isCollapsed) {
      setHasUnreadMessages(true);
    }
    previousMessageCount.current = chatMessages.length;
  }, [chatMessages, isCollapsed]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newCollapsed = !prev;
      if (prev) {
        // When expanding, clear unread messages
        setHasUnreadMessages(false);
      }
      return newCollapsed;
    });
  }, []);

  // Listen for typing indicators
  useEffect(() => {
    if (!socket) return;

    const handleUserTyping = (data: { username: string; isTyping: boolean }) => {
      setTypingUsers(prev => {
        if (data.isTyping) {
          return prev.includes(data.username) ? prev : [...prev, data.username];
        } else {
          return prev.filter(user => user !== data.username);
        }
      });
    };

    socket.on('userTyping', handleUserTyping);

    return () => {
      socket.off('userTyping', handleUserTyping);
    };
  }, [socket]);

  // Keyboard shortcut to toggle chat (Ctrl/Cmd + M)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'm') {
        e.preventDefault();
        toggleCollapse();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleCollapse]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    
    sendChatMessage(draftId, newMessage);
    setNewMessage('');
    stopTyping(draftId);
    
    // Clear typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    // Handle typing indicators
    if (e.target.value.length > 0) {
      startTyping(draftId);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(draftId);
      }, 2000);
    } else {
      stopTyping(draftId);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ChatContainer isCollapsed={isCollapsed}>
      <ChatHeader onClick={toggleCollapse} isCollapsed={isCollapsed}>
        <ChatTitle>
          <MessageCircle size={20} />
          Draft Chat
          {chatMessages.length > 0 && (
            <span style={{ 
              fontSize: typography.fontSize.xs, 
              color: colors.grey[400],
              marginLeft: spacing[2]
            }}>
              ({chatMessages.length})
            </span>
          )}
          {hasUnreadMessages && (
            <span style={{ 
              fontSize: typography.fontSize.xs, 
              color: colors.error,
              marginLeft: spacing[2],
              fontWeight: typography.fontWeight.bold
            }}>
              • NEW
            </span>
          )}
        </ChatTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <ActiveUsersCount>
            <OnlineDot />
            <Users size={14} />
            {activeUsers.length} online
          </ActiveUsersCount>
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </div>
      </ChatHeader>

      <ChatMessages isCollapsed={isCollapsed}>
        {chatMessages.length === 0 ? (
          <EmptyState>
            <EmptyIcon />
            <EmptyTitle>No messages yet</EmptyTitle>
            <EmptyCopy>Kick things off with a quick strategy note.</EmptyCopy>
          </EmptyState>
        ) : (
          chatMessages.map((message, index) => (
            <MessageItem key={index} isOwnMessage={message.user.username === currentUsername}>
              <MessageHeader>
                <Username role={message.user.role}>
                  {message.user.username}
                </Username>
                <Timestamp>
                  {formatTimestamp(message.timestamp)}
                </Timestamp>
              </MessageHeader>
              <MessageBubble isOwnMessage={message.user.username === currentUsername}>
                {message.message}
              </MessageBubble>
            </MessageItem>
          ))
        )}
        
        {typingUsers.length > 0 && (
          <TypingIndicator>
            <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing</span>
            <TypingDots>
              <Dot delay={0} />
              <Dot delay={0.2} />
              <Dot delay={0.4} />
            </TypingDots>
          </TypingIndicator>
        )}
        
        <div ref={messagesEndRef} />
      </ChatMessages>

      <ChatInput isCollapsed={isCollapsed}>
        <MessageInput
          ref={inputRef}
          type="text"
          placeholder="Type your message..."
          value={newMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          maxLength={500}
        />
        <SendButton
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
        >
          <Send size={16} />
          <SendLabel>Send</SendLabel>
        </SendButton>
      </ChatInput>
    </ChatContainer>
  );
};

export default Chat;
